import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EnterToNextDirective } from '../../../../../directives/enter-to-next.directive';
interface SnapshotRow {
  metal: string;
  opening: string;
  sold: string;
  received: string;
  balance: string;
  balanceType: 'DR' | 'CR';
}

/* ================= INTERFACES ================= */

export interface MetalLedgerSnapshot {
  metal: 'GOLD' | 'SILVER' | 'STONE' | 'AMT';
  openingBalance: number;
  openingBalanceType?: 'DR' | 'CR';
  receivedFine: number;
  soldFine: number;
  preSettlementBalance?: number; // ✅ Stable baseline for popup
  balance: number;
  balanceType: 'DR' | 'CR';
  currRate: number;
}

/* ================= COMPONENT ================= */

@Component({
  selector: 'app-rate-cut-no-rate-cut',
  standalone: true,
  imports: [CommonModule, FormsModule, EnterToNextDirective],
  templateUrl: './rate-cut-no-rate-cut.component.html'
})
export class RateCutNoRateCutComponent implements OnChanges {

  /* ========= INPUT / OUTPUT ========= */

  @Input() ledgerData: MetalLedgerSnapshot[] = [];
  @Input() historyRows: any[] = [];
  @Output() closeComponent = new EventEmitter<void>();
  @Output() submitRateCut = new EventEmitter<any>();


  /* ========= CARD UI ========= */

  cards: any[] = [];
  selectedCard: any = null;

  /* ========= TABLE ========= */

  tableData: any[] = [];

  metalOptions = ['SILVER', 'AMT'];
  transactionOptions = ['Balance', 'Receive'];

  activeMetalDropdownIndex: number | null = null;
  activeTransactionRowIndex: number | null = null;
  readonlySnapshotRows: SnapshotRow[] = [];


  /* ================= INIT ================= */

  ngOnChanges(): void {
    this.buildCardsFromLedger();
    this.buildReadonlySnapshot(); // ✅ ADD THIS


    // Load history if it exists, otherwise start fresh
    if (this.historyRows && this.historyRows.length > 0 && this.tableData.length === 0) {
      this.tableData = JSON.parse(JSON.stringify(this.historyRows));
      this.recalculateLedger();
    } else if (this.tableData.length === 0) {
      this.buildInitialTable();
    } else {
      // 🔥 If table is already open, we MUST recalculate to pick up new preSettlementBalance
      this.recalculateLedger();
    }
  }

  /* ================= CARDS ================= */

  private buildCardsFromLedger(): void {
    const silver = this.ledgerData.find(l => l.metal === 'SILVER');
    if (!silver) {
      this.cards = [];
      return;
    }

    // Cast silver to any to access the custom properties we added in the parent
    const s = silver as any;

    this.cards = [{
      id: 'silver',
      title: 'SILVER',
      outerBg: '#F8F6F4',
      innerBg: '#FBFBFB',
      iconUrl: 'assets/images/rate-cut-no-rate-cut/Silver.png',
      balanceLabel: 'SL BALANCE',
      balanceVal: Math.abs(silver.balance).toFixed(2),
      balanceUnit: 'GM',
      balanceSuffix: silver.balanceType,
      rows: [
        // ✅ AMOUNT PREVIOUS BALANCE
        {
          label: 'SL Previous Bal',
          val: this.ledgerData.find(l => l.metal === 'AMT')?.balance.toFixed(2) || '0.00',
          unit: '₹',
          color: 'red',
          suffix: this.ledgerData.find(l => l.metal === 'AMT')?.balanceType,
          suffixColor: 'text-red-500'
        },

        // ✅ RATE CUT CASH ONLY
        {
          label: 'Cash SL Bal',
          val: (s.cashSlBal || 0).toFixed(2),
          unit: '₹',
          color: 'black'
        },

        // ✅ RATE CUT METAL WT
        {
          label: 'Cash SL WT',
          val: (s.cashSlWt || 0).toFixed(3),
          unit: 'GM',
          color: 'black'
        },

        { label: 'SL Sell', val: silver.soldFine.toFixed(2), unit: 'GM', color: 'black' },
        { label: 'SL Received', val: silver.receivedFine.toFixed(2), unit: 'GM', color: 'green' }
      ]

    }];
  }

  private buildReadonlySnapshot(): void {
    this.readonlySnapshotRows = (this.ledgerData || []).map(l => ({
      metal: l.metal,
      opening: l.metal === 'AMT' ? '—' : Number(l.openingBalance || 0).toFixed(2),
      sold: l.metal === 'AMT' ? '—' : Number(l.soldFine || 0).toFixed(2),
      received: l.metal === 'AMT' ? '—' : Number(l.receivedFine || 0).toFixed(2),
      balance: Number(l.balance || 0).toFixed(2),
      balanceType: l.balanceType || 'DR'
    }));
  }


  /* ================= TABLE INIT ================= */

  private buildInitialTable(): void {
    const silver = this.ledgerData.find(l => l.metal === 'SILVER');
    if (!silver) return;

    const baseline = silver.preSettlementBalance || 0;

    this.tableData = [
      this.createBalanceRow(Math.abs(baseline), baseline >= 0 ? 'DR' : 'CR')
    ];
  }

  /* ================= ROW FACTORIES ================= */

  private createBalanceRow(balance: number, type: 'DR' | 'CR' = 'DR'): any {
    return {
      transaction: 'Balance',
      isPurchaseRow: false,
      isBalanceRow: true,
      isMetalDropdown: false,
      metal: 'SILVER',
      category: 'Metal',
      name: 'SILVER',
      gsWt: balance.toFixed(2),
      purity: '100',
      fineWt: balance.toFixed(2),
      metalAmt: '',
      rate: '',
      amount: '',
      finalBalance: balance.toFixed(2),
      finalBalanceType: type
    };
  }

  private createActionRow(): any {
    return {
      transaction: 'Receive',
      isPurchaseRow: false,
      isBalanceRow: false,
      isMetalDropdown: true,
      currencyMode: 'METAL',
      metal: 'SILVER',
      category: 'Metal',
      name: 'SILVER',
      gsWt: '',
      purity: '100',
      fineWt: '',
      metalAmt: '',
      rate: '',
      amount: '',
      finalBalance: '',
      finalBalanceType: '',
      _needsPatch: false
    };
  }

  /* ================= LEDGER CALC ================= */

  recalculateLedger(): void {
    const silver = this.ledgerData.find(l => l.metal === 'SILVER');
    if (!silver) return;

    // 1. Calculate Baseline (Balance before ANY settlement rows in THIS popup table)
    let runningBalance = silver.preSettlementBalance || 0;

    for (let i = 0; i < this.tableData.length; i++) {
      const row = this.tableData[i];
      let settlementWeight = 0;

      if (row.transaction === 'Balance' && i === 0) {
        // First row is always the starting balance
        const balVal = Math.abs(runningBalance).toFixed(2);
        row.gsWt = balVal;
        row.fineWt = balVal;
        row.finalBalance = balVal;
        row.finalBalanceType = runningBalance >= 0 ? 'DR' : 'CR';
        continue;
      }

      if (row.transaction === 'Sell' || row.transaction === 'Receive') {
        const isReceive = row.transaction === 'Receive';

        if (row.currencyMode === 'AMT') {
          // --- PATCH LOGIC ONLY FOR AMT RECEIVE ---
          if (isReceive) {
            if (row._needsPatch || !row.gsWt || row.gsWt === '0.00') {
              row.gsWt = Math.abs(runningBalance).toFixed(2);
              row._needsPatch = false;
            }
            // CALCULATION: GS WT * RATE = AMOUNT
            if (row.gsWt && row.rate && Number(row.rate) > 0) {
              row.amount = (Number(row.gsWt) * Number(row.rate)).toFixed(2);
              row.metalAmt = Number(row.gsWt).toFixed(2);
              row.fineWt = Number(row.gsWt).toFixed(2);
              settlementWeight = Number(row.gsWt);
            } else {
              row.metalAmt = '';
              row.fineWt = '';
              row.amount = '';
            }
          } else {
            // --- CASH TO METAL (Sell/Fixing) ---
            if (row.amount && row.rate && Number(row.rate) > 0) {
              settlementWeight = Number(row.amount) / Number(row.rate);
              row.metalAmt = settlementWeight.toFixed(2);
              row.fineWt = settlementWeight.toFixed(2);
            } else {
              row.metalAmt = '';
              row.fineWt = '';
            }
          }
        } else {
          // --- METAL TO CASH (SILVER CASE) ---
          let physicalWt = 0;
          let hasPhysical = false;

          if (row.gsWt && !isNaN(Number(row.gsWt)) && Number(row.gsWt) !== 0) {
            const purity = row.purity && !isNaN(Number(row.purity)) ? Number(row.purity) : 0;
            physicalWt = (Number(row.gsWt) * purity) / 100;
            hasPhysical = true;
          }

          if (hasPhysical) {
            settlementWeight = physicalWt;
          } else if (row.metalAmt && !isNaN(Number(row.metalAmt))) {
            settlementWeight = Number(row.metalAmt);
          }

          row.fineWt = settlementWeight > 0 ? settlementWeight.toFixed(2) : '';

          let conversionWt = (row.metalAmt && !isNaN(Number(row.metalAmt))) ? Number(row.metalAmt) : settlementWeight;

          if (conversionWt > 0 && row.rate && row.rate !== '') {
            row.amount = (conversionWt * Number(row.rate)).toFixed(2);
          }
        }

        row.finalBalance = settlementWeight > 0 ? settlementWeight.toFixed(2) : '';
        row.finalBalanceType = 'CR';

        // Subtract from debt if Receive, Add back to debt if Sell
        runningBalance -= (isReceive ? settlementWeight : -settlementWeight);
      }
      // else if (row.transaction === 'Pay') {
      //   settlementWeight = Number(row.fineWt || 0);
      //   row.finalBalance = settlementWeight > 0 ? settlementWeight.toFixed(2) : '';

      //   // 🔥 PRACTICAL LOGIC: Paying metal to customer is a DEBIT (DR)
      //   row.finalBalanceType = 'DR';

      //   // Increase debt
      //   runningBalance += settlementWeight;
      // }
      else if (row.isBalanceRow) {
        const balanceVal = Math.abs(runningBalance).toFixed(2);

        row.gsWt = balanceVal;
        row.fineWt = balanceVal;
        row.finalBalance = balanceVal;

        // 🔥 PRACTICAL LOGIC: Positive = Customer owes us (DR), Negative = We owe them (CR)
        row.finalBalanceType = runningBalance >= 0 ? 'DR' : 'CR';

        row.amount = '';
        row.rate = '';
        row.metalAmt = '';
      }
    }
  }

  displayValue(value: any): string {
    if (value === null || value === undefined || value === '' || value === 0) {
      return ''; // Return empty string instead of dash for a cleaner look
    }
    const num = Number(value);
    return isNaN(num) ? '' : num.toFixed(2);
  }

  // 🔥 ADD THIS: Formats input fields only when the user is done (on Blur)
  formatInputOnBlur(row: any, field: string): void {
    if (row[field] && row[field] !== '' && !isNaN(Number(row[field]))) {
      row[field] = Number(row[field]).toFixed(2);
    }
    this.recalculateLedger();
  }


  private getLastRate(beforeIndex: number): number {
    for (let i = beforeIndex - 1; i >= 0; i--) {
      const r = this.tableData[i];
      if (r.rate && Number(r.rate) > 0) {
        return Number(r.rate);
      }
    }
    return Number(
      this.ledgerData.find(l => l.metal === 'SILVER')?.currRate || 0
    );
  }

  /* ================= ROW ADD (CONTROLLED) ================= */

  addNextRowIfNeeded(index: number): void {
    const row = this.tableData[index];
    if (!row || row.isBalanceRow || row.isPurchaseRow) return;

    if (row._rowCompleted) return;

    let completed = false;

    if (row.currencyMode === 'AMT') {
      completed = !!row.amount && !!row.rate;
    } else {
      completed = !!row.fineWt;
    }

    if (!completed) return;
    if (this.tableData[index + 1]) return;

    row._rowCompleted = true;

    this.tableData.push(this.createBalanceRow(Number(row.finalBalance)));
    this.tableData.push(this.createActionRow());
  }


  /* ================= DROPDOWNS (HTML SAFE) ================= */

  toggleTransactionDropdown(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.activeTransactionRowIndex =
      this.activeTransactionRowIndex === index ? null : index;
  }

  selectTransaction(row: any, value: string): void {

    const wasBalance = row.isBalanceRow;

    row.transaction = value;
    row.isBalanceRow = value === 'Balance';
    row.isPurchaseRow = value === 'Sell';

    row.isMetalDropdown = (value === 'Receive' || value === 'Pay')

    // 🔥 When Balance → Receive / Sell
    if (wasBalance && value !== 'Balance') {
      row.currencyMode = 'METAL';
      row.metal = 'SILVER';
      row.gsWt = '';
      row.metalAmt = '';
      row.amount = '';
      row.rate = '';
    }

    // Auto insert balance row
    if (wasBalance && value !== 'Balance') {
      const index = this.tableData.indexOf(row);
      const next = this.tableData[index + 1];
      if (!next || !next.isBalanceRow) {
        this.tableData.splice(index + 1, 0, this.createBalanceRow(0));
      }
    }

    this.activeTransactionRowIndex = null;
    this.recalculateLedger();
  }



  toggleMetalDropdown(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.activeMetalDropdownIndex =
      this.activeMetalDropdownIndex === index ? null : index;
  }

  selectMetal(row: any, value: string): void {

    if (value === 'AMT') {
      row.currencyMode = 'AMT';
      row.metal = 'AMT';
      row._needsPatch = true; // Trigger balance patch in recalculateLedger

      row.rate = this.getLastRate(
        this.tableData.indexOf(row)
      );

      row.gsWt = '';
      row.metalAmt = '';
      row.fineWt = '';
      row.purity = '100';

      row.amount = '';
    }
    else {
      row.currencyMode = 'METAL';
      row.metal = value;

      // Clear amount inputs
      row.amount = '';
    }

    this.activeMetalDropdownIndex = null;
    this.recalculateLedger();
  }


  getMetalBgColor(_: string): string {
    return 'bg-gray-400';
  }

  getTransactionClass(type: string): string {
    switch (type) {
      case 'Sell': return 'bg-purple-50 text-purple-700 border border-purple-200';
      case 'Balance': return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'Receive': return 'bg-green-50 text-green-700 border border-green-200';
      // case 'Pay': return 'bg-red-50 text-red-700 border border-red-200';
      default: return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  }

  /* ================= UI ================= */

  openPopup(card: any): void {
    this.selectedCard = card;
  }

  closePopup(): void {
    this.selectedCard = null;
  }

  goBack(): void {
    this.closeComponent.emit();
  }

  get totalMetalAmt(): number {
    return this.tableData.reduce((sum, r) => {
      return sum + (Number(r.metalAmt) || 0);
    }, 0);
  }

  get totalAmount(): number {
    return this.tableData.reduce((sum, r) => {
      return sum + (Number(r.amount) || 0);
    }, 0);
  }

  get totalFineWt(): number {
    return this.tableData.reduce((sum, r) => {
      return sum + (Number(r.fineWt) || 0);
    }, 0);
  }


  onSubmitRateCut(): void {
    let metalFineDelta = 0; // Pure metal received (Silver mode)
    let fixedMetalDelta = 0;    // Metal weight converted to cash (Amount mode)
    let cashAmountDelta = 0;
    let salesDelta = 0;
    let receiptsDelta = 0;

    for (const row of this.tableData) {
      if (row.transaction === 'Receive' || row.transaction === 'Sell') {
        const isReceive = row.transaction === 'Receive';

        // Impact on metal balance: Receive reduces debt (positive delta for 'received' calculation)
        const fineWt = Number(row.fineWt || 0);
        const signedFineWt = isReceive ? fineWt : -fineWt;

        const amt = Number(row.amount || 0);
        const signedAmt = isReceive ? amt : -amt;

        // Separate physical weight from fixed weight
        if (row.currencyMode === 'AMT') {
          fixedMetalDelta += signedFineWt;
          cashAmountDelta += signedAmt;
        } else {
          metalFineDelta += signedFineWt;
        }

        if (row.transaction === 'Sell') {
          salesDelta += amt;
        } else {
          receiptsDelta += amt;
        }
      }
    }

    this.submitRateCut.emit({
      metal: 'SILVER',
      metalFineDelta,
      fixedMetalDelta,
      cashAmountDelta,
      salesDelta,
      receiptsDelta,
      rows: this.tableData
    });

    this.selectedCard = null;
  }



  @HostListener('document:click')
  closeDropdowns(): void {
    this.activeMetalDropdownIndex = null;
    this.activeTransactionRowIndex = null;
  }
}